import React, { useState, useEffect } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_BASE_URL } from '../config/api';
import { Card, Image, Descriptions, message, Spin, Input, Button, InputNumber, Form, Alert } from 'antd';
import './css/CollarPage.css';

const { TextArea } = Input;

const CollarPage = () => {
  const { sku } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [quantity, setQuantity] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [form] = Form.useForm();

  // Get cat information from location state
  const catInfo = location.state?.cat;

  useEffect(() => {
    // If no cat info, redirect back to cat list
    if (!catInfo) {
      message.error('请先选择需要定制项圈的猫咪');
      navigate('/cats');
      return;
    }
  }, [catInfo, navigate]);

  const token = localStorage.getItem('token');
  const headers = token ? { 'Authorization': `Token ${token}` } : {};

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        console.log('Fetching product with SKU:', sku);
        const response = await axios.get(`${API_BASE_URL}/api/business/products/?sku=${sku}`, { headers });
        console.log('Product data:', response.data);
        
        const products = response.data;
        if (products && products.length > 0) {
          setProduct(products[0]);
        } else {
          message.error('Product not found');
        }
      } catch (error) {
        console.error('Error fetching product:', error);
        message.error('Failed to fetch product details');
      } finally {
        setLoading(false);
      }
    };

    if (sku) {
      fetchProduct();
    }
  }, [sku]);

  const handleSubmitOrder = async (values) => {
    if (!product) {
      message.error('Product information not available');
      return;
    }

    setSubmitting(true);
    try {
      const orderData = {
        product: product.id,
        cat: catInfo.id,
        shipping_address: values.shippingAddress,
        total_amount: (product.price * quantity).toFixed(2),
        notes: values.notes || ''
      };

      const response = await axios.post(
        `${API_BASE_URL}/api/business/orders/`,
        orderData,
        { headers }
      );

      message.success(`订单创建成功！订单号: ${response.data.order_number}`);
      form.resetFields();
      setQuantity(1);
    } catch (error) {
      console.error('Error submitting order:', error);
      message.error(error.response?.data?.message || '订单提交失败，请重试');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="loading-container">
        <Spin size="large" />
      </div>
    );
  }

  if (!product) {
    return (
      <div className="error-container">
        <h2>Product not found</h2>
        <p>The requested product could not be found.</p>
      </div>
    );
  }

  return (
    <div className="collar-page">
      <div className="product-container">
        <Alert
          message={`为 ${catInfo.name} (ID: ${catInfo.unique_id}) 定制项圈`}
          type="info"
          showIcon
          style={{ marginBottom: '20px' }}
        />
        <div className="product-image-section">
          {product.image ? (
            <Image
              src={product.image}
              alt={product.name}
              className="product-image"
            />
          ) : (
            <div className="no-image">No image available</div>
          )}
        </div>
        <div className="product-details-section">
          <h1 className="product-title">{product.name}</h1>
          <div className="product-price">
            <span className="currency">¥</span>
            <span className="amount">{product.price}</span>
          </div>
          
          <div className="product-info">
            <Descriptions bordered column={1}>
              <Descriptions.Item label="SKU">{product.sku_code}</Descriptions.Item>
              {product.variation && (
                <Descriptions.Item label="规格">{product.variation}</Descriptions.Item>
              )}
              <Descriptions.Item label="库存">{product.quantity}</Descriptions.Item>
              <Descriptions.Item label="描述">{product.description}</Descriptions.Item>
            </Descriptions>
          </div>

          <Form
            form={form}
            layout="vertical"
            onFinish={handleSubmitOrder}
            className="order-form"
          >
            <div className="quantity-selector">
              <label>数量:</label>
              <InputNumber
                min={1}
                max={product.quantity}
                value={quantity}
                onChange={setQuantity}
                className="quantity-input"
              />
            </div>

            <Form.Item
              label="收货地址"
              name="shippingAddress"
              rules={[{ required: true, message: '请输入收货地址！' }]}
            >
              <TextArea
                rows={4}
                placeholder="请输入详细的收货地址"
                className="shipping-address-input"
              />
            </Form.Item>

            <Form.Item
              label="备注"
              name="notes"
            >
              <TextArea
                rows={2}
                placeholder="可选：添加订单备注"
                className="notes-input"
              />
            </Form.Item>

            <div className="order-summary">
              <div className="total-price">
                总计: ¥{(product.price * quantity).toFixed(2)}
              </div>
              <Button 
                type="primary" 
                htmlType="submit" 
                className="submit-order-btn"
                loading={submitting}
                disabled={submitting}
              >
                确认订单
              </Button>
            </div>
          </Form>
        </div>
      </div>
    </div>
  );
};

export default CollarPage;
